import { Link } from "react-router-dom";
import { Phone, Stethoscope, HandHeart, Activity, ShieldCheck, Star, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { COMPANY, IMG } from "@/lib/constants";
import { AREAS } from "@/data/areas";
import { LeadForm } from "@/components/LeadForm";
import { ZipChecker } from "@/components/ZipChecker";
import { Reveal } from "@/components/Reveal";

const SERVICES = [
  { icon: Stethoscope, title: "Skilled Nursing", desc: "Wound care, medication management, IV therapy and clinical assessments delivered at home by licensed RNs.", to: "/services/skilled-nursing" },
  { icon: Activity, title: "Therapy Services", desc: "Rehab-quality Physical, Occupational, and Speech therapy to restore strength, mobility and independence.", to: "/services/physical-therapy" },
  { icon: HandHeart, title: "Personal Care", desc: "Compassionate private duty aides assist with bathing, grooming, meals, and companionship.", to: "/services/personal-care" },
  { icon: ShieldCheck, title: "Medical Social Work", desc: "Guidance navigating resources, insurance, and long-term care planning for your family.", to: "/services/medical-social-worker" },
];

const TESTIMONIALS = [
  { quote: "With Esprit, I don't have to leave the comfort of my home for the best personal care experience.", name: "Scott C." },
  { quote: "From the time I got discharged from the hospital, till I got home, it was a pleasant and smooth experience with Esprit.", name: "Wanda S." },
  { quote: "Esprit's clinical staff were not only professional, but compassionate too. They made me feel safe and secure.", name: "Susan B." },
];

export default function Home() {
  useSEO({
    title: "Esprit Home Healthcare | Compassionate Home Care in Northern Virginia",
    description: "Skilled nursing, therapy, and personal care delivered at home across Northern Virginia. One call to (703) 998-7400 starts your care.",
    canonical: "/",
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[hsl(var(--secondary))] text-white grain">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-primary/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-accent/20 blur-3xl pointer-events-none" />
        <div className="container-page relative grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 text-white px-4 py-1.5 text-sm font-semibold tracking-wide">
              <MapPin className="w-4 h-4 text-accent" /> Serving Northern Virginia
            </span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mt-5">
              Compassionate Care, <span className="text-accent">Right at Home.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/75 mt-6 leading-relaxed max-w-xl">
              Nobody makes it easier to start home care. One quick call gets you Skilled Nursing, Therapy, and Personal Care, whatever it takes to provide the best possible care at home.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <a href={COMPANY.phoneHref} data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-16 px-8 text-xl font-bold transition-colors">
                <Phone className="w-6 h-6" /> Call {COMPANY.phone}
              </a>
              <Link to="/services" data-testid="hero-services-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/30 text-white hover:bg-white hover:text-secondary h-16 px-8 text-xl font-bold transition-colors">
                Our Services <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-white/70">
              {["Preferred by major insurers", "24/7 On-Call Support", "Licensed & Compassionate"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 text-base">
                  <CheckCircle2 className="w-5 h-5 text-accent" /> {t}
                </span>
              ))}
            </div>
          </div>
          <div className="animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative mb-0 sm:mb-16 lg:mb-10">
              {/* offset decorative accent frame for a distinctive, editorial look */}
              <div className="absolute -top-4 -right-4 w-full h-full rounded-[2rem] border-2 border-accent/40 pointer-events-none hidden sm:block" />
              <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-white/10">
                <video
                  data-testid="hero-video"
                  className="w-full h-[420px] lg:h-[520px] object-cover"
                  src="/hero-care.mp4"
                  poster={IMG.hero}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label="Caregiver supporting an elderly patient at home"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/50 via-transparent to-transparent pointer-events-none" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-black/35 backdrop-blur-sm text-white px-3 py-1.5 text-sm font-medium">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
                  </span>
                  Real care, every day
                </div>
              </div>
              <div className="mt-5 sm:mt-0 sm:absolute sm:-bottom-10 sm:-left-6">
                <ZipChecker />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="container-page py-20 lg:py-28">
        <Reveal className="text-center max-w-3xl mx-auto">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-secondary">Our Services</p>
          <h2 className="font-heading text-4xl md:text-5xl text-primary mt-3">A One-Stop Shop for Home Health Care</h2>
          <p className="text-lg text-muted-foreground mt-4">
            We're specialists serving Northern Virginia patients with a broad range of needs, from skilled clinical care to everyday personal support.
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <Link to={s.to} data-testid={`service-card-${i}`}
                className="group block h-full bg-card rounded-2xl border border-border p-7 hover:border-primary hover:-translate-y-1 transition-all duration-300">
                <span className="grid place-items-center w-14 h-14 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <s.icon className="w-7 h-7" />
                </span>
                <h3 className="font-heading text-2xl text-primary mt-5">{s.title}</h3>
                <p className="text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
                <span className="inline-flex items-center gap-1 text-primary font-semibold mt-4 group-hover:gap-2 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* About split */}
      <section className="bg-[hsl(var(--muted))]">
        <div className="container-page grid lg:grid-cols-2 gap-12 items-center py-20">
          <Reveal>
            <img src={IMG.about} alt="Elderly couple smiling together at home"
              className="rounded-[2rem] w-full h-[400px] object-cover shadow-lg" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-secondary">Why Esprit</p>
            <h2 className="font-heading text-4xl md:text-5xl text-primary mt-3">Esprit Makes It Easy</h2>
            <p className="text-lg text-foreground/80 mt-5 leading-relaxed">
              One quick call to Esprit and our friendly staff will assist you in getting Skilled Nursing, Therapy (Physical, Occupational, and Speech), Medical Social Worker services, and Personal Care including Private Duty services.
            </p>
            <p className="text-lg text-foreground/80 mt-4 leading-relaxed">
              We are a preferred provider for most major insurance plans, and if we can't take it, we'll help place it. We are the care experts, so let us take care of making all your arrangements.
            </p>
            <Link to="/about" data-testid="about-learn-more"
              className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-7 text-lg font-bold mt-7 transition-colors">
              About Us <ArrowRight className="w-5 h-5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Testimonials */}
      <section className="container-page py-20 lg:py-28">
        <Reveal className="text-center max-w-3xl mx-auto">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-secondary">Testimonials</p>
          <h2 className="font-heading text-4xl md:text-5xl text-primary mt-3">What Patients & Families Say</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <figure data-testid={`testimonial-${i}`} className="h-full bg-card rounded-2xl border border-border p-8">
                <div className="flex gap-1 text-accent mb-4">
                  {[...Array(5)].map((_, k) => <Star key={k} className="w-5 h-5 fill-accent" />)}
                </div>
                <blockquote className="font-heading text-2xl text-foreground leading-snug italic">"{t.quote}"</blockquote>
                <figcaption className="mt-5 font-semibold text-primary">— {t.name}</figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Service Areas strip */}
      <section className="bg-primary text-primary-foreground">
        <div className="container-page py-16 text-center">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-accent">Service Areas</p>
          <h2 className="font-heading text-4xl md:text-5xl mt-3">Proudly Serving Northern Virginia</h2>
          <div className="flex flex-wrap justify-center gap-3 mt-8 max-w-4xl mx-auto">
            {AREAS.map((a) => (
              <Link key={a.slug} to={`/service-areas/${a.slug}`} className="rounded-full bg-primary-foreground/10 hover:bg-accent hover:text-accent-foreground px-5 py-2 text-base font-medium transition-colors">{a.name}</Link>
            ))}
          </div>
          <Link to="/service-areas" data-testid="view-service-areas"
            className="inline-flex items-center gap-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-14 px-8 text-lg font-bold mt-10 transition-colors">
            View All Service Areas <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Lead form */}
      <section id="contact-form" className="container-page py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-secondary">We're Here To Help</p>
            <h2 className="font-heading text-4xl md:text-5xl text-primary mt-3">Let's Build the Right Care Plan Together</h2>
            <p className="text-lg text-muted-foreground mt-5 leading-relaxed">
              Fill out the form and a member of our team will contact you to design the right care-giving plan for you and your family. Choosing a homecare provider is an important decision, and we're here to make it simple.
            </p>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-3 mt-8 group">
              <span className="grid place-items-center w-16 h-16 rounded-2xl bg-primary text-primary-foreground group-hover:scale-105 transition-transform">
                <Phone className="w-8 h-8" />
              </span>
              <span>
                <span className="block text-sm text-muted-foreground">Call us directly</span>
                <span className="block font-heading text-3xl text-primary">{COMPANY.phone}</span>
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-[hsl(var(--muted))] rounded-[2rem] border border-border p-6 sm:p-8">
              <LeadForm source="home-page" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
